/**
 * Skylab API — Insights snapshot.
 *
 * GET /internal/skylab/insights/:schoolId?division=all|lower|middle|upper
 *
 * Super-admin-only. Computes a per-school diagnostic snapshot from already-synced
 * Blackbaud SKY data: section publishing activity, content density by division,
 * communications channel coverage, coverage gaps, content-type mix, and content
 * that isn't reaching parents. The whole snapshot is computed in one pass and
 * returned as a single envelope (plan Decision 3) — the Insights tab and the
 * Report tab both render from this same payload.
 *
 * AI is STUBBED this phase (plan Decision 7): per-card `narrative` and
 * `executiveSummary` are real string fields on the envelope but are populated by
 * a placeholder generator, not a model. The contract does not change when a real
 * LLM is wired in later.
 *
 * Type names are prefixed `Skylab*` because the root shared-types barrel
 * re-exports every `api/*` module with `export *`; the prefix avoids collisions.
 */

import { z } from 'zod'

/**
 * Division a section belongs to. `unknown` covers sections whose school-level
 * name doesn't map to a Lower/Middle/Upper bucket (the mapping is a hard-coded
 * heuristic this phase — see backend `division.ts`). `unknown` sections still
 * count toward school-wide totals but are excluded from division comparisons.
 */
export const SkylabDivisionSchema = z.enum(['lower', 'middle', 'upper', 'unknown'])
export type SkylabDivision = z.infer<typeof SkylabDivisionSchema>

/** The division toggle on the Insights tab. `all` = no division restriction. */
export const SkylabDivisionFilterSchema = z.enum(['all', 'lower', 'middle', 'upper'])
export type SkylabDivisionFilter = z.infer<typeof SkylabDivisionFilterSchema>

/** Named divisions that appear in comparison cards (excludes `unknown`/`all`). */
export const SkylabNamedDivisionSchema = z.enum(['lower', 'middle', 'upper'])
export type SkylabNamedDivision = z.infer<typeof SkylabNamedDivisionSchema>

// ── Card data shapes ────────────────────────────────────────────────────────

/** One section row in the publishing heatmap: a count per week bucket. */
export const SkylabHeatmapSectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  division: SkylabDivisionSchema,
  /** Parent-facing items published in each week bucket; aligns with `weekStarts`. */
  cells: z.array(z.number().int().nonnegative()),
  /** Total across all weeks (cells summed). */
  total: z.number().int().nonnegative(),
  /** True when the section published nothing in the whole window. */
  silent: z.boolean()
})
export type SkylabHeatmapSection = z.infer<typeof SkylabHeatmapSectionSchema>

export const SkylabPublishingHeatmapDataSchema = z.object({
  /** ISO start-of-week timestamps, oldest → newest; column axis of the heatmap. */
  weekStarts: z.array(z.string()),
  /** Sections shown in the grid (capped; see `hiddenSectionCount`). */
  sections: z.array(SkylabHeatmapSectionSchema),
  totalSections: z.number().int().nonnegative(),
  silentSections: z.number().int().nonnegative(),
  /** Sections not rendered because the grid is capped for readability. */
  hiddenSectionCount: z.number().int().nonnegative()
})
export type SkylabPublishingHeatmapData = z.infer<typeof SkylabPublishingHeatmapDataSchema>

export const SkylabDivisionDensitySchema = z.object({
  division: SkylabNamedDivisionSchema,
  sectionCount: z.number().int().nonnegative(),
  /** Mean parent-facing items per week per section in this division. */
  itemsPerWeekPerSection: z.number().nonnegative(),
  /** Share of this division's sections that were silent in the window (0–100). */
  silentPercent: z.number().min(0).max(100)
})
export type SkylabDivisionDensity = z.infer<typeof SkylabDivisionDensitySchema>

export const SkylabDensityByDivisionDataSchema = z.object({
  divisions: z.array(SkylabDivisionDensitySchema)
})
export type SkylabDensityByDivisionData = z.infer<typeof SkylabDensityByDivisionDataSchema>

/** Whether a comms channel is visible to the SKY API. */
export const SkylabChannelStatusSchema = z.enum(['api', 'dark', 'partial'])
export type SkylabChannelStatus = z.infer<typeof SkylabChannelStatusSchema>

export const SkylabChannelSchema = z.object({
  key: z.string(),
  label: z.string(),
  status: SkylabChannelStatusSchema,
  /** Sub-label explaining a dark/partial channel; null for plain API channels. */
  detail: z.string().nullable(),
  /** Sends in the last 30 days where measurable; null when the channel is dark. */
  sentLast30d: z.number().int().nonnegative().nullable()
})
export type SkylabChannel = z.infer<typeof SkylabChannelSchema>

export const SkylabChannelMapDataSchema = z.object({
  channels: z.array(SkylabChannelSchema),
  darkCount: z.number().int().nonnegative()
})
export type SkylabChannelMapData = z.infer<typeof SkylabChannelMapDataSchema>

export const SkylabCoverageDivisionSchema = z.object({
  division: SkylabNamedDivisionSchema,
  sectionCount: z.number().int().nonnegative(),
  silentCount: z.number().int().nonnegative(),
  silentPercent: z.number().min(0).max(100)
})
export type SkylabCoverageDivision = z.infer<typeof SkylabCoverageDivisionSchema>

export const SkylabCoverageGapsDataSchema = z.object({
  /** Recency window the silence is measured over (days). */
  windowDays: z.number().int().positive(),
  overallSilentPercent: z.number().min(0).max(100),
  divisions: z.array(SkylabCoverageDivisionSchema)
})
export type SkylabCoverageGapsData = z.infer<typeof SkylabCoverageGapsDataSchema>

export const SkylabContentTypeSliceSchema = z.object({
  /** Normalized type key, e.g. `announcement`, `news`, `event`, `post`. */
  type: z.string(),
  label: z.string(),
  count: z.number().int().nonnegative(),
  /** Share of all parent-facing items (0–100). */
  percent: z.number().min(0).max(100)
})
export type SkylabContentTypeSlice = z.infer<typeof SkylabContentTypeSliceSchema>

export const SkylabContentTypeMixDataSchema = z.object({
  total: z.number().int().nonnegative(),
  mix: z.array(SkylabContentTypeSliceSchema)
})
export type SkylabContentTypeMixData = z.infer<typeof SkylabContentTypeMixDataSchema>

/** Severity of an item that isn't reaching parents. */
export const SkylabUnreachedSeveritySchema = z.enum(['error', 'warning'])
export type SkylabUnreachedSeverity = z.infer<typeof SkylabUnreachedSeveritySchema>

export const SkylabUnreachedItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** Why it isn't reaching parents (audience misconfig, no parent contacts, …). */
  reason: z.string(),
  severity: SkylabUnreachedSeveritySchema,
  sectionName: z.string().nullable()
})
export type SkylabUnreachedItem = z.infer<typeof SkylabUnreachedItemSchema>

export const SkylabUnreachedContentDataSchema = z.object({
  count: z.number().int().nonnegative(),
  items: z.array(SkylabUnreachedItemSchema)
})
export type SkylabUnreachedContentData = z.infer<typeof SkylabUnreachedContentDataSchema>

// ── Card envelope ─────────────────────────────────────────────────────────────

/** Stable card identifiers (mirror the plan's U2 card list for traceability). */
export const SKYLAB_CARD_IDS = [
  'publishing-heatmap',
  'density-by-division',
  'channel-map',
  'coverage-gaps',
  'content-type-mix',
  'unreached-content'
] as const

/**
 * A single Insights card. `headline` is the bold one-line stat; `narrative` is
 * the AI one-liner (placeholder this phase). `data` is the card's typed slice.
 */
const cardSchema = <Id extends string, Data extends z.ZodTypeAny>(id: z.ZodLiteral<Id>, data: Data) =>
  z.object({
    id,
    title: z.string(),
    headline: z.string(),
    narrative: z.string(),
    data
  })

export const SkylabInsightsSnapshotSchema = z.object({
  /** School public id the snapshot was computed for. */
  schoolId: z.string(),
  schoolName: z.string(),
  /** ISO timestamp the snapshot was computed at (cache provenance). */
  computedAt: z.string(),
  divisionFilter: SkylabDivisionFilterSchema,
  /** Number of week buckets the window spans. */
  windowWeeks: z.number().int().positive(),
  totals: z.object({
    sections: z.number().int().nonnegative(),
    parentFacingItems: z.number().int().nonnegative()
  }),
  cards: z.object({
    publishingHeatmap: cardSchema(z.literal('publishing-heatmap'), SkylabPublishingHeatmapDataSchema),
    densityByDivision: cardSchema(z.literal('density-by-division'), SkylabDensityByDivisionDataSchema),
    channelMap: cardSchema(z.literal('channel-map'), SkylabChannelMapDataSchema),
    coverageGaps: cardSchema(z.literal('coverage-gaps'), SkylabCoverageGapsDataSchema),
    contentTypeMix: cardSchema(z.literal('content-type-mix'), SkylabContentTypeMixDataSchema),
    unreachedContent: cardSchema(z.literal('unreached-content'), SkylabUnreachedContentDataSchema)
  }),
  /** Long-form summary for the Report tab. Placeholder string this phase. */
  executiveSummary: z.string()
})
export type SkylabInsightsSnapshot = z.infer<typeof SkylabInsightsSnapshotSchema>

/** Per-card types, indexed for convenient consumer access. */
export type SkylabInsightsCards = SkylabInsightsSnapshot['cards']
